import {
  PUBLIC_TRANSPORT_TUNING,
  PUBLIC_TRANSPORT_VEHICLE_ASSETS,
} from "../../config/publicTransport.ts";
import type { PlayerId } from "../../core/ids.ts";
import type { InputIntent } from "../../input/intents.ts";
import {
  VILNIUS_PUBLIC_TRANSPORT_PATTERNS,
  publicTransportPatternById,
  publicTransportStopById,
  type PublicTransportPatternStop,
  type PublicTransportRoutePattern,
} from "../../world/publicTransportRoutes.ts";
import type { VilniusPublicTransportMode } from "../../world/busStops.ts";
import type { GameState } from "../state.ts";

export const PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION = 1 as const;

/**
 * Safety ceiling for the accelerated gameplay timetable. Every segment keeps
 * enough deterministic travel time to cover its shape distance at no more
 * than 72 km/h before an occupied vehicle's explicit Shift boost is applied.
 */
export const PUBLIC_TRANSPORT_MAXIMUM_SERVICE_SPEED_MPS = 20;

export interface PublicTransportMount {
  readonly vehicleId: string;
  readonly patternId: string;
  readonly scheduledDepartureSeconds: number;
  readonly boardedStopIndex: number;
  readonly alightRequested: boolean;
}

export interface PublicTransportFleetState {
  readonly schemaVersion: typeof PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION;
  readonly tick: number;
  readonly serviceTimeSeconds: number;
  readonly boostedVehicleId: string | null;
  readonly mountByPlayerId: Readonly<Record<PlayerId, PublicTransportMount>>;
}

export interface PublicTransportVehiclePresentation {
  readonly id: string;
  readonly patternId: string;
  readonly sourceRouteId: string;
  readonly mode: VilniusPublicTransportMode;
  readonly routeShortName: string;
  readonly routeLongName: string;
  readonly headsign: string;
  readonly routeColor: string;
  readonly routeTextColor: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly speedMps: number;
  readonly distanceAlongShapeM: number;
  readonly doorsOpen: boolean;
  readonly currentStopIndex: number | null;
  readonly currentStopId: string | null;
  readonly nextStopIndex: number | null;
  readonly nextStopId: string | null;
  readonly occupied: boolean;
}

export interface PublicTransportQueries {
  groundHeight(
    position: Readonly<{ x: number; z: number }>,
    travelYawRadians?: number,
  ): number | null;
  resolveExit(request: Readonly<{
    vehicleId: string;
    candidates: readonly Readonly<{ x: number; y: number; z: number }>[];
  }>): Readonly<{ x: number; y: number; z: number }> | null;
}

export interface PublicTransportPlayerOverride {
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly facingYaw: number;
  readonly surfaceId: string;
  readonly vehicleId: string | null;
  readonly speedMps: number;
  readonly mode: VilniusPublicTransportMode | null;
  readonly routeShortName: string | null;
  readonly headsign: string | null;
}

export interface PublicTransportFleetStepResult {
  readonly state: PublicTransportFleetState;
  readonly transition:
    | "boarded"
    | "stop-requested"
    | "alighted"
    | "terminal-alighted"
    | null;
  readonly interactionConsumed: boolean;
  readonly blockedExit: boolean;
  readonly playerOverride: PublicTransportPlayerOverride | null;
}

interface OperationalStop extends PublicTransportPatternStop {
  readonly operationalArrivalSeconds: number;
  readonly operationalDepartureSeconds: number;
}

interface TripPose {
  readonly vehicleId: string;
  readonly pattern: Readonly<PublicTransportRoutePattern>;
  readonly scheduledDepartureSeconds: number;
  readonly x: number;
  readonly z: number;
  readonly yawRadians: number;
  readonly speedMps: number;
  readonly distanceAlongShapeM: number;
  readonly doorsOpen: boolean;
  readonly currentStopIndex: number | null;
  readonly nextStopIndex: number | null;
  readonly terminal: boolean;
}

const operationalStopsByPatternId = new Map<string, readonly OperationalStop[]>();
const operationalDurationByPatternId = new Map<string, number>();

function operationalStops(
  pattern: Readonly<PublicTransportRoutePattern>,
): readonly OperationalStop[] {
  const cached = operationalStopsByPatternId.get(pattern.id);
  if (cached !== undefined) return cached;
  const stops: OperationalStop[] = [];
  for (const [stopIndex, stop] of pattern.stops.entries()) {
    const scheduledDwellSeconds = Math.max(
      0,
      stop.departureOffsetSeconds - stop.arrivalOffsetSeconds,
    );
    const dwellSeconds = Math.max(
      PUBLIC_TRANSPORT_TUNING.minimumStopDwellSeconds,
      scheduledDwellSeconds,
    );
    let operationalArrivalSeconds = stop.arrivalOffsetSeconds;
    const previous = stops.at(-1);
    if (previous !== undefined) {
      const previousSourceStop = pattern.stops[stopIndex - 1]!;
      const segmentDistanceM =
        stop.distanceAlongShapeM - previous.distanceAlongShapeM;
      const minimumTravelSeconds =
        segmentDistanceM / PUBLIC_TRANSPORT_MAXIMUM_SERVICE_SPEED_MPS;
      const scheduledTravelSeconds = Math.max(
        0,
        stop.arrivalOffsetSeconds - previousSourceStop.departureOffsetSeconds,
      );
      const gameplayTravelSeconds = Math.max(
        minimumTravelSeconds,
        scheduledTravelSeconds /
          PUBLIC_TRANSPORT_TUNING.betweenStopSpeedMultiplier,
      );
      operationalArrivalSeconds =
        previous.operationalDepartureSeconds + gameplayTravelSeconds;
    }
    const operationalDepartureSeconds = operationalArrivalSeconds + dwellSeconds;
    stops.push(Object.freeze({
      ...stop,
      operationalArrivalSeconds,
      operationalDepartureSeconds,
    }));
  }
  const frozen = Object.freeze(stops);
  operationalStopsByPatternId.set(pattern.id, frozen);
  operationalDurationByPatternId.set(
    pattern.id,
    stops.at(-1)?.operationalDepartureSeconds ?? 0,
  );
  return frozen;
}

function operationalDurationSeconds(
  pattern: Readonly<PublicTransportRoutePattern>,
): number {
  operationalStops(pattern);
  return operationalDurationByPatternId.get(pattern.id) ?? 0;
}

function lowerBound(values: readonly number[], target: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (values[middle]! < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function absoluteDeparturesNear(
  pattern: Readonly<PublicTransportRoutePattern>,
  serviceTimeSeconds: number,
): readonly number[] {
  const daySeconds = PUBLIC_TRANSPORT_TUNING.serviceDaySeconds;
  const duration = operationalDurationSeconds(pattern) +
    PUBLIC_TRANSPORT_TUNING.terminalHoldSeconds;
  const minimum = serviceTimeSeconds - duration;
  const firstOperationalArrivalSeconds =
    operationalStops(pattern)[0]?.operationalArrivalSeconds ?? 0;
  const maximum = serviceTimeSeconds - Math.min(
    0,
    firstOperationalArrivalSeconds,
  );
  const firstDay = Math.floor(minimum / daySeconds) - 1;
  const lastDay = Math.floor(maximum / daySeconds);
  const departures: number[] = [];
  for (let day = firstDay; day <= lastDay; day += 1) {
    const dayOffset = day * daySeconds;
    const relativeMinimum = minimum - dayOffset;
    const relativeMaximum = maximum - dayOffset;
    let index = lowerBound(pattern.departuresSeconds, relativeMinimum);
    while (
      index < pattern.departuresSeconds.length &&
      pattern.departuresSeconds[index]! <= relativeMaximum
    ) {
      departures.push(dayOffset + pattern.departuresSeconds[index]!);
      index += 1;
    }
  }
  return departures;
}

function vehicleId(
  pattern: Readonly<PublicTransportRoutePattern>,
  scheduledDepartureSeconds: number,
): string {
  return `${pattern.id}:trip:${scheduledDepartureSeconds}`;
}

function shapePoseAtDistance(
  pattern: Readonly<PublicTransportRoutePattern>,
  requestedDistanceM: number,
): Readonly<{ x: number; z: number; yawRadians: number }> {
  const shape = pattern.shape;
  const maximumDistanceM = shape.at(-1)?.distanceM ?? 0;
  const distanceM = clamp(requestedDistanceM, 0, maximumDistanceM);
  let low = 1;
  let high = shape.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (shape[middle]!.distanceM < distanceM) low = middle + 1;
    else high = middle;
  }
  const to = shape[Math.max(1, low)]!;
  const from = shape[Math.max(0, low - 1)]!;
  const segmentDistanceM = Math.max(
    Number.EPSILON,
    to.distanceM - from.distanceM,
  );
  const alpha = clamp(
    (distanceM - from.distanceM) / segmentDistanceM,
    0,
    1,
  );
  return {
    x: lerp(from.x, to.x, alpha),
    z: lerp(from.z, to.z, alpha),
    yawRadians: Math.atan2(to.x - from.x, to.z - from.z),
  };
}

function tripPose(
  pattern: Readonly<PublicTransportRoutePattern>,
  scheduledDepartureSeconds: number,
  serviceTimeSeconds: number,
  keepTerminal = false,
): TripPose | null {
  const elapsedSeconds = serviceTimeSeconds - scheduledDepartureSeconds;
  const stops = operationalStops(pattern);
  const duration = operationalDurationSeconds(pattern);
  const terminalEnd = duration + PUBLIC_TRANSPORT_TUNING.terminalHoldSeconds;
  const firstOperationalArrivalSeconds =
    stops[0]?.operationalArrivalSeconds ?? 0;
  if (
    elapsedSeconds < firstOperationalArrivalSeconds ||
    (!keepTerminal && elapsedSeconds > terminalEnd)
  ) {
    return null;
  }
  const id = vehicleId(pattern, scheduledDepartureSeconds);
  if (elapsedSeconds >= duration) {
    const stopIndex = stops.length - 1;
    const stop = stops[stopIndex]!;
    const pose = shapePoseAtDistance(pattern, stop.distanceAlongShapeM);
    return {
      vehicleId: id,
      pattern,
      scheduledDepartureSeconds,
      ...pose,
      speedMps: 0,
      distanceAlongShapeM: stop.distanceAlongShapeM,
      doorsOpen: true,
      currentStopIndex: stopIndex,
      nextStopIndex: null,
      terminal: true,
    };
  }
  for (let index = 0; index < stops.length; index += 1) {
    const stop = stops[index]!;
    if (
      elapsedSeconds >= stop.operationalArrivalSeconds &&
      elapsedSeconds <= stop.operationalDepartureSeconds
    ) {
      const pose = shapePoseAtDistance(pattern, stop.distanceAlongShapeM);
      return {
        vehicleId: id,
        pattern,
        scheduledDepartureSeconds,
        ...pose,
        speedMps: 0,
        distanceAlongShapeM: stop.distanceAlongShapeM,
        doorsOpen: true,
        currentStopIndex: index,
        nextStopIndex: index + 1 < stops.length ? index + 1 : null,
        terminal: index === stops.length - 1,
      };
    }
    const next = stops[index + 1];
    if (next === undefined) break;
    if (
      elapsedSeconds > stop.operationalDepartureSeconds &&
      elapsedSeconds < next.operationalArrivalSeconds
    ) {
      const travelSeconds = Math.max(
        Number.EPSILON,
        next.operationalArrivalSeconds - stop.operationalDepartureSeconds,
      );
      const progress = clamp(
        (elapsedSeconds - stop.operationalDepartureSeconds) / travelSeconds,
        0,
        1,
      );
      const distanceAlongShapeM = lerp(
        stop.distanceAlongShapeM,
        next.distanceAlongShapeM,
        progress,
      );
      const pose = shapePoseAtDistance(pattern, distanceAlongShapeM);
      return {
        vehicleId: id,
        pattern,
        scheduledDepartureSeconds,
        ...pose,
        speedMps:
          (next.distanceAlongShapeM - stop.distanceAlongShapeM) / travelSeconds,
        distanceAlongShapeM,
        doorsOpen: false,
        currentStopIndex: null,
        nextStopIndex: index + 1,
        terminal: false,
      };
    }
  }
  return null;
}

function presentationsNear(
  state: Readonly<PublicTransportFleetState>,
  focus: Readonly<{ x: number; z: number }>,
  groundHeight: PublicTransportQueries["groundHeight"],
  radiusM: number,
  maximumCount: number,
): readonly PublicTransportVehiclePresentation[] {
  const mountedIds = new Set(
    Object.values(state.mountByPlayerId).map((mount) => mount.vehicleId),
  );
  const candidates: Array<{
    presentation: PublicTransportVehiclePresentation;
    distanceM: number;
  }> = [];
  for (const pattern of VILNIUS_PUBLIC_TRANSPORT_PATTERNS) {
    for (const departure of absoluteDeparturesNear(
      pattern,
      state.serviceTimeSeconds,
    )) {
      const pose = tripPose(pattern, departure, state.serviceTimeSeconds);
      if (pose === null) continue;
      const distanceM = Math.hypot(pose.x - focus.x, pose.z - focus.z);
      if (distanceM > radiusM && !mountedIds.has(pose.vehicleId)) continue;
      const y = groundHeight({ x: pose.x, z: pose.z }, pose.yawRadians);
      if (y === null && !mountedIds.has(pose.vehicleId)) continue;
      candidates.push({
        distanceM,
        presentation: presentationForPose(
          pose,
          y ?? 0,
          mountedIds.has(pose.vehicleId),
          state.boostedVehicleId === pose.vehicleId
            ? boostMultiplierForPose(pose)
            : 1,
        ),
      });
    }
  }
  for (const mount of Object.values(state.mountByPlayerId)) {
    if (candidates.some(({ presentation }) => presentation.id === mount.vehicleId)) {
      continue;
    }
    const pattern = publicTransportPatternById(mount.patternId);
    if (pattern === null) continue;
    const pose = tripPose(
      pattern,
      mount.scheduledDepartureSeconds,
      state.serviceTimeSeconds,
      true,
    );
    if (pose === null) continue;
    candidates.push({
      distanceM: 0,
      presentation: presentationForPose(
        pose,
        groundHeight({ x: pose.x, z: pose.z }, pose.yawRadians) ?? 0,
        true,
        state.boostedVehicleId === pose.vehicleId
          ? boostMultiplierForPose(pose)
          : 1,
      ),
    });
  }
  return candidates
    .sort((left, right) =>
      Number(right.presentation.occupied) - Number(left.presentation.occupied) ||
      left.distanceM - right.distanceM ||
      left.presentation.id.localeCompare(right.presentation.id)
    )
    .slice(0, maximumCount)
    .map(({ presentation }) => presentation);
}

function presentationForPose(
  pose: Readonly<TripPose>,
  groundY: number,
  occupied: boolean,
  speedMultiplier = 1,
): PublicTransportVehiclePresentation {
  const stops = operationalStops(pose.pattern);
  return {
    id: pose.vehicleId,
    patternId: pose.pattern.id,
    sourceRouteId: pose.pattern.sourceRouteId,
    mode: pose.pattern.mode,
    routeShortName: pose.pattern.routeShortName,
    routeLongName: pose.pattern.routeLongName,
    headsign: pose.pattern.headsign,
    routeColor: pose.pattern.routeColor,
    routeTextColor: pose.pattern.routeTextColor,
    position: {
      x: pose.x,
      y: groundY + PUBLIC_TRANSPORT_TUNING.routeVehicleGroundLiftM,
      z: pose.z,
    },
    yawRadians: pose.yawRadians,
    speedMps: pose.speedMps * speedMultiplier,
    distanceAlongShapeM: pose.distanceAlongShapeM,
    doorsOpen: pose.doorsOpen,
    currentStopIndex: pose.currentStopIndex,
    currentStopId: pose.currentStopIndex === null
      ? null
      : stops[pose.currentStopIndex]?.stopId ?? null,
    nextStopIndex: pose.nextStopIndex,
    nextStopId: pose.nextStopIndex === null
      ? null
      : stops[pose.nextStopIndex]?.stopId ?? null,
    occupied,
  };
}

function boostMultiplierForPose(pose: Readonly<TripPose>): number {
  if (pose.doorsOpen || pose.speedMps <= 0) return 1;
  return Math.max(
    1,
    Math.min(
      PUBLIC_TRANSPORT_TUNING.passengerBoostSpeedMultiplier,
      PUBLIC_TRANSPORT_TUNING.passengerBoostMaximumSpeedMps / pose.speedMps,
    ),
  );
}

export function createInitialPublicTransportFleetState(): PublicTransportFleetState {
  return {
    schemaVersion: PUBLIC_TRANSPORT_FLEET_SCHEMA_VERSION,
    tick: 0,
    serviceTimeSeconds: PUBLIC_TRANSPORT_TUNING.initialServiceTimeSeconds,
    boostedVehicleId: null,
    mountByPlayerId: {},
  };
}

export function clearPublicTransportOccupancy(
  state: Readonly<PublicTransportFleetState>,
): PublicTransportFleetState {
  return { ...state, boostedVehicleId: null, mountByPlayerId: {} };
}

export function publicTransportPresentations(
  state: Readonly<PublicTransportFleetState>,
  focus: Readonly<{ x: number; z: number }>,
  groundHeight: PublicTransportQueries["groundHeight"],
): readonly PublicTransportVehiclePresentation[] {
  return presentationsNear(
    state,
    focus,
    groundHeight,
    PUBLIC_TRANSPORT_TUNING.renderRadiusM,
    PUBLIC_TRANSPORT_TUNING.maximumRenderedVehicles,
  );
}

export function publicTransportColliderPresentations(
  state: Readonly<PublicTransportFleetState>,
  focus: Readonly<{ x: number; z: number }>,
  groundHeight: PublicTransportQueries["groundHeight"],
): readonly PublicTransportVehiclePresentation[] {
  return presentationsNear(
    state,
    focus,
    groundHeight,
    PUBLIC_TRANSPORT_TUNING.colliderRadiusM,
    PUBLIC_TRANSPORT_TUNING.maximumColliderVehicles,
  );
}

/**
 * Produces one scheduled trip at an exact service-clock time. This is useful
 * for deterministic review scenarios without changing the fleet clock or
 * duplicating route interpolation outside the simulation.
 */
export function publicTransportTripPresentationAtTime(
  patternId: string,
  scheduledDepartureSeconds: number,
  serviceTimeSeconds: number,
  groundY = 0,
): PublicTransportVehiclePresentation | null {
  const pattern = publicTransportPatternById(patternId);
  if (
    pattern === null ||
    !Number.isFinite(scheduledDepartureSeconds) ||
    !Number.isFinite(serviceTimeSeconds) ||
    !Number.isFinite(groundY)
  ) {
    return null;
  }
  const pose = tripPose(pattern, scheduledDepartureSeconds, serviceTimeSeconds);
  return pose === null ? null : presentationForPose(pose, groundY, false);
}

function nearestBoardablePose(
  state: Readonly<PublicTransportFleetState>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  groundHeight: PublicTransportQueries["groundHeight"],
): TripPose | null {
  let nearest: { pose: TripPose; distanceM: number } | null = null;
  for (const pattern of VILNIUS_PUBLIC_TRANSPORT_PATTERNS) {
    for (const departure of absoluteDeparturesNear(
      pattern,
      state.serviceTimeSeconds,
    )) {
      const pose = tripPose(pattern, departure, state.serviceTimeSeconds);
      if (
        pose === null ||
        !pose.doorsOpen ||
        pose.currentStopIndex === null ||
        pose.terminal
      ) {
        continue;
      }
      const vehicleDistanceM = Math.hypot(
        pose.x - playerPosition.x,
        pose.z - playerPosition.z,
      );
      const currentStop = operationalStops(pattern)[pose.currentStopIndex];
      if (currentStop === undefined) continue;
      const platformDistanceM = Math.hypot(
        currentStop.position.x - playerPosition.x,
        currentStop.position.z - playerPosition.z,
      );
      const distanceM = Math.min(vehicleDistanceM, platformDistanceM);
      if (distanceM > PUBLIC_TRANSPORT_TUNING.boardingRadiusM) continue;
      const groundY = groundHeight(
        { x: pose.x, z: pose.z },
        pose.yawRadians,
      );
      if (
        groundY !== null &&
        Math.abs(groundY - playerPosition.y) >
          PUBLIC_TRANSPORT_TUNING.maximumBoardingHeightDifferenceM
      ) {
        continue;
      }
      if (nearest === null || distanceM < nearest.distanceM) {
        nearest = { pose, distanceM };
      }
    }
  }
  return nearest?.pose ?? null;
}

function mountedPose(
  state: Readonly<PublicTransportFleetState>,
  playerId: PlayerId,
): { mount: PublicTransportMount; pose: TripPose } | null {
  const mount = state.mountByPlayerId[playerId];
  if (mount === undefined) return null;
  const pattern = publicTransportPatternById(mount.patternId);
  if (pattern === null) return null;
  const pose = tripPose(
    pattern,
    mount.scheduledDepartureSeconds,
    state.serviceTimeSeconds,
    true,
  );
  return pose === null ? null : { mount, pose };
}

function playerOverrideForPose(
  pose: Readonly<TripPose>,
  groundHeight: PublicTransportQueries["groundHeight"],
  fallbackY: number,
  speedMultiplier = 1,
): PublicTransportPlayerOverride {
  const local = PUBLIC_TRANSPORT_TUNING.passengerLocalPosition;
  const cosine = Math.cos(pose.yawRadians);
  const sine = Math.sin(pose.yawRadians);
  const groundY =
    groundHeight({ x: pose.x, z: pose.z }, pose.yawRadians) ?? fallbackY;
  return {
    position: {
      x: pose.x + local.x * cosine + local.z * sine,
      y: groundY + local.y,
      z: pose.z - local.x * sine + local.z * cosine,
    },
    facingYaw: pose.yawRadians,
    surfaceId: `public-transport:${pose.vehicleId}`,
    vehicleId: pose.vehicleId,
    speedMps: pose.speedMps * speedMultiplier,
    mode: pose.pattern.mode,
    routeShortName: pose.pattern.routeShortName,
    headsign: pose.pattern.headsign,
  };
}

/**
 * Resolves the authoritative passenger pose for a mounted player.
 *
 * Startup restore and runtime composition use the same transform as the fixed
 * simulation step, so a saved passenger cannot briefly appear at the stop or
 * at the vehicle origin while the first tick is being prepared.
 */
export function publicTransportPlayerOverride(
  state: Readonly<PublicTransportFleetState>,
  playerId: PlayerId,
  groundHeight: PublicTransportQueries["groundHeight"],
  fallbackY: number,
): PublicTransportPlayerOverride | null {
  const mounted = mountedPose(state, playerId);
  return mounted === null
    ? null
    : playerOverrideForPose(
      mounted.pose,
      groundHeight,
      fallbackY,
      state.boostedVehicleId === mounted.pose.vehicleId
        ? boostMultiplierForPose(mounted.pose)
        : 1,
    );
}

function exitCandidates(
  pose: Readonly<TripPose>,
  stop: Readonly<OperationalStop>,
  groundHeight: PublicTransportQueries["groundHeight"],
  fallbackY: number,
): readonly Readonly<{ x: number; y: number; z: number }>[] {
  const stopDeltaX = stop.position.x - pose.x;
  const stopDeltaZ = stop.position.z - pose.z;
  const stopDistanceM = Math.hypot(stopDeltaX, stopDeltaZ);
  const exitDirectionX = stopDistanceM > 0.1
    ? stopDeltaX / stopDistanceM
    : Math.cos(pose.yawRadians);
  const exitDirectionZ = stopDistanceM > 0.1
    ? stopDeltaZ / stopDistanceM
    : -Math.sin(pose.yawRadians);
  const firstOffsetM =
    PUBLIC_TRANSPORT_VEHICLE_ASSETS[pose.pattern.mode].dimensionsM.x / 2 +
    PUBLIC_TRANSPORT_TUNING.exitClearanceBeyondBodyM;
  const curbOffsetM = Math.max(firstOffsetM, stopDistanceM - 0.65);
  const candidateStepM = Math.max(
    PUBLIC_TRANSPORT_TUNING.exitCandidateStepM,
    (curbOffsetM - firstOffsetM) /
      Math.max(1, PUBLIC_TRANSPORT_TUNING.exitCandidateCount - 1),
  );
  return Array.from(
    { length: PUBLIC_TRANSPORT_TUNING.exitCandidateCount },
    (_, index) => {
      const offsetM = Math.max(
        firstOffsetM,
        curbOffsetM - index * candidateStepM,
      );
      const position = {
        x: pose.x + exitDirectionX * offsetM,
        z: pose.z + exitDirectionZ * offsetM,
      };
      return {
        ...position,
        y:
          groundHeight(position, pose.yawRadians) ??
          groundHeight(stop.position, pose.yawRadians) ??
          fallbackY,
      };
    },
  );
}

function alight(
  state: Readonly<PublicTransportFleetState>,
  intent: Readonly<InputIntent>,
  mounted: Readonly<{ mount: PublicTransportMount; pose: TripPose }>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  queries: Readonly<PublicTransportQueries>,
  terminal: boolean,
): PublicTransportFleetStepResult | null {
  const stopIndex = mounted.pose.currentStopIndex;
  const stop = stopIndex === null
    ? null
    : operationalStops(mounted.pose.pattern)[stopIndex] ?? null;
  if (stop === null) return null;
  const exit = queries.resolveExit({
    vehicleId: mounted.pose.vehicleId,
    candidates: exitCandidates(
      mounted.pose,
      stop,
      queries.groundHeight,
      playerPosition.y,
    ),
  });
  if (exit === null) {
    return {
      state,
      transition: null,
      interactionConsumed: intent.interactPressed,
      blockedExit: true,
      playerOverride: playerOverrideForPose(
        mounted.pose,
        queries.groundHeight,
        playerPosition.y,
      ),
    };
  }
  const mounts = { ...state.mountByPlayerId };
  delete mounts[intent.playerId];
  return {
    state: { ...state, boostedVehicleId: null, mountByPlayerId: mounts },
    transition: terminal ? "terminal-alighted" : "alighted",
    interactionConsumed: intent.interactPressed,
    blockedExit: false,
    playerOverride: {
      position: exit,
      facingYaw: mounted.pose.yawRadians,
      surfaceId: `public-transport-stop:${stop.stopId}`,
      vehicleId: null,
      speedMps: 0,
      mode: null,
      routeShortName: null,
      headsign: null,
    },
  };
}

export function stepPublicTransportFleet(
  state: Readonly<PublicTransportFleetState>,
  intent: Readonly<InputIntent>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  deltaSeconds: number,
  queries: Readonly<PublicTransportQueries>,
): PublicTransportFleetStepResult {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    throw new Error("Public-transport deltaSeconds must be positive");
  }
  let next: PublicTransportFleetState = {
    ...state,
    tick: intent.tick,
    serviceTimeSeconds: state.serviceTimeSeconds + deltaSeconds,
    boostedVehicleId: null,
  };
  let mounted = mountedPose(next, intent.playerId);
  if (mounted === null) {
    if (!intent.interactPressed) {
      return {
        state: next,
        transition: null,
        interactionConsumed: false,
        blockedExit: false,
        playerOverride: null,
      };
    }
    const candidate = nearestBoardablePose(
      next,
      playerPosition,
      queries.groundHeight,
    );
    if (candidate === null) {
      return {
        state: next,
        transition: null,
        interactionConsumed: false,
        blockedExit: false,
        playerOverride: null,
      };
    }
    const stopIndex = candidate.currentStopIndex;
    if (stopIndex === null) throw new Error("Boardable transit must be at a stop");
    const mount: PublicTransportMount = {
      vehicleId: candidate.vehicleId,
      patternId: candidate.pattern.id,
      scheduledDepartureSeconds: candidate.scheduledDepartureSeconds,
      boardedStopIndex: stopIndex,
      alightRequested: false,
    };
    next = {
      ...next,
      mountByPlayerId: { ...next.mountByPlayerId, [intent.playerId]: mount },
    };
    return {
      state: next,
      transition: "boarded",
      interactionConsumed: true,
      blockedExit: false,
      playerOverride: playerOverrideForPose(
        candidate,
        queries.groundHeight,
        playerPosition.y,
      ),
    };
  }

  if (intent.sprintHeld && !mounted.pose.doorsOpen && mounted.pose.speedMps > 0) {
    const boostMultiplier = boostMultiplierForPose(mounted.pose);
    next = {
      ...next,
      serviceTimeSeconds:
        next.serviceTimeSeconds + deltaSeconds * (boostMultiplier - 1),
      boostedVehicleId: mounted.pose.vehicleId,
    };
    mounted = mountedPose(next, intent.playerId);
    if (mounted === null) {
      throw new Error("Boosted public-transport mount disappeared");
    }
    if (mounted.pose.doorsOpen || mounted.pose.speedMps <= 0) {
      next = { ...next, boostedVehicleId: null };
    }
  }

  const shouldAlightAtRequestedStop =
    mounted.mount.alightRequested &&
    mounted.pose.doorsOpen &&
    mounted.pose.currentStopIndex !== null &&
    mounted.pose.currentStopIndex > mounted.mount.boardedStopIndex;
  const shouldAlightNow = intent.interactPressed && mounted.pose.doorsOpen;
  if (shouldAlightAtRequestedStop || shouldAlightNow || mounted.pose.terminal) {
    const result = alight(
      next,
      intent,
      mounted,
      playerPosition,
      queries,
      mounted.pose.terminal,
    );
    if (result !== null) return result;
  }

  let transition: PublicTransportFleetStepResult["transition"] = null;
  if (intent.interactPressed && !mounted.pose.doorsOpen) {
    next = {
      ...next,
      mountByPlayerId: {
        ...next.mountByPlayerId,
        [intent.playerId]: { ...mounted.mount, alightRequested: true },
      },
    };
    transition = "stop-requested";
  }
  return {
    state: next,
    transition,
    interactionConsumed: intent.interactPressed,
    blockedExit: false,
    playerOverride: playerOverrideForPose(
      mounted.pose,
      queries.groundHeight,
      playerPosition.y,
      next.boostedVehicleId === mounted.pose.vehicleId
        ? boostMultiplierForPose(mounted.pose)
        : 1,
    ),
  };
}

export function mountedPublicTransport(
  state: Readonly<PublicTransportFleetState>,
  playerId: PlayerId,
): Readonly<{
  mount: PublicTransportMount;
  presentation: PublicTransportVehiclePresentation;
  boostActive: boolean;
}> | null {
  const mounted = mountedPose(state, playerId);
  if (mounted === null) return null;
  return {
    mount: mounted.mount,
    presentation: presentationForPose(
      mounted.pose,
      0,
      true,
      state.boostedVehicleId === mounted.pose.vehicleId
        ? boostMultiplierForPose(mounted.pose)
        : 1,
    ),
    boostActive: state.boostedVehicleId === mounted.pose.vehicleId,
  };
}

export function publicTransportInteractionPrompt(
  state: Readonly<PublicTransportFleetState>,
  playerId: PlayerId,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  groundHeight: PublicTransportQueries["groundHeight"],
): string | null {
  const mounted = mountedPose(state, playerId);
  if (mounted !== null) {
    const stops = operationalStops(mounted.pose.pattern);
    const current = mounted.pose.currentStopIndex === null
      ? null
      : stops[mounted.pose.currentStopIndex] ?? null;
    const next = mounted.pose.nextStopIndex === null
      ? null
      : stops[mounted.pose.nextStopIndex] ?? null;
    const currentName = current === null
      ? null
      : publicTransportStopById(current.stopId)?.name ?? current.stopId;
    const nextName = next === null
      ? mounted.pose.pattern.headsign
      : publicTransportStopById(next.stopId)?.name ?? next.stopId;
    if (mounted.pose.doorsOpen && currentName !== null) {
      return `Get off at ${currentName} · ${mounted.pose.pattern.routeShortName} toward ${mounted.pose.pattern.headsign}`;
    }
    if (mounted.mount.alightRequested) {
      return `Stop requested · ${nextName} · Shift boost`;
    }
    return `${mounted.pose.pattern.routeShortName} toward ${mounted.pose.pattern.headsign} · next ${nextName} · E request stop · Shift boost`;
  }
  const candidate = nearestBoardablePose(state, playerPosition, groundHeight);
  if (candidate === null) return null;
  const vehicleLabel = candidate.pattern.mode === "bus" ? "bus" : "trolleybus";
  return `Board ${vehicleLabel} ${candidate.pattern.routeShortName} toward ${candidate.pattern.headsign}`;
}

export function applyPublicTransportPlayerTick(
  state: Readonly<GameState>,
  intent: Readonly<InputIntent>,
  override: Readonly<PublicTransportPlayerOverride>,
  deltaSeconds: number,
): GameState {
  const player = state.playersById[intent.playerId];
  if (player === undefined) {
    throw new Error(`Player ${intent.playerId} is missing from game state`);
  }
  return {
    ...state,
    tick: intent.tick,
    simulationTimeSeconds: state.simulationTimeSeconds + deltaSeconds,
    playersById: {
      ...state.playersById,
      [intent.playerId]: {
        ...player,
        position: { ...override.position },
        velocity: { x: 0, y: 0, z: 0 },
        facingYaw: override.facingYaw,
        lookYaw: intent.lookYaw,
        isSprinting: false,
        locomotion: {
          ...player.locomotion,
          mode: "grounded",
          grounded: true,
          groundNormal: { x: 0, y: 1, z: 0 },
          groundSlopeRadians: 0,
          surfaceId: override.surfaceId,
          coyoteTicksRemaining: 0,
          jumpBufferTicksRemaining: 0,
          landingResponseTicksRemaining: 0,
          mantleProgress: 0,
          footstepDistanceMetres: 0,
          lastStepHeightMetres: 0,
        },
      },
    },
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * alpha;
}
